import { Search, UserCheck } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/ui/components/ui/badge';
import { Button } from '@/ui/components/ui/button';
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '@/ui/components/ui/card';
import { Input } from '@/ui/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/ui/components/ui/select';
import { StatusPlaceholder } from '@/ui/components/ui/status-placeholder';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/ui/components/ui/table';
import { useAdminAuth } from '../../context/AdminAuthContext';
import type { AccountObject, ProjectMember } from '../../hooks/use-admin';
import {
	useAdminAccounts,
	useAdminAddProjectMember,
	useAdminProjectMembers,
	useAdminRemoveProjectMember,
	useAdminUpdateAccountRole,
} from '../../hooks/use-admin';
import { formatEnum } from '../../utils/format-enum';

interface UserAccessTabProps {
	projectId: string;
}

export function UserAccessTab({ projectId }: UserAccessTabProps) {
	const { user: currentUser } = useAdminAuth();
	const isCurrentUserAdmin = currentUser?.role === 'admin';
	const { data: accountsData, loading: accountsLoading } = useAdminAccounts();
	const { data: membersData, loading: membersLoading } =
		useAdminProjectMembers(projectId);
	const [addMember] = useAdminAddProjectMember();
	const [removeMember] = useAdminRemoveProjectMember();
	const [updateRole] = useAdminUpdateAccountRole();

	const [search, setSearch] = useState('');
	const [pendingId, setPendingId] = useState<string | null>(null);
	const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);

	const accounts: AccountObject[] = accountsData?.adminAccounts ?? [];
	const members: ProjectMember[] = membersData?.adminProjectMembers ?? [];
	const memberAccountIds = new Set(members.map((m) => m.accountId));

	const filtered = search.trim()
		? accounts.filter((a) => {
				const q = search.toLowerCase();
				return (
					a.email.toLowerCase().includes(q) ||
					(a.name ?? '').toLowerCase().includes(q) ||
					a.role.toLowerCase().includes(q)
				);
			})
		: accounts;

	const handleAdd = async (accountId: string, email: string) => {
		setPendingId(accountId);
		try {
			await addMember({
				variables: {
					input: { projectId, email, role: 'MODERATOR' },
				},
			});
		} finally {
			setPendingId(null);
		}
	};

	const handleRemove = async (accountId: string) => {
		setPendingId(accountId);
		try {
			await removeMember({
				variables: { input: { projectId, accountId } },
			});
		} finally {
			setPendingId(null);
		}
	};

	const handleRoleChange = async (accountId: string, role: string) => {
		setPendingRoleId(accountId);
		try {
			await updateRole({ variables: { input: { accountId, role } } });
		} finally {
			setPendingRoleId(null);
		}
	};

	const loading = accountsLoading || membersLoading;

	return (
		<Card>
			<CardHeader className='pb-3'>
				<div className='flex items-center justify-between gap-4'>
					<CardTitle className='text-sm font-medium'>
						All users
						{accounts.length > 0 && (
							<span className='ml-2 text-muted-foreground font-normal'>
								({accounts.length})
							</span>
						)}
					</CardTitle>
					{accounts.length > 0 && (
						<div className='relative w-56'>
							<Search className='absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none' />
							<Input
								placeholder='Search by name, email, role…'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className='pl-8 h-8 text-sm'
							/>
						</div>
					)}
				</div>
			</CardHeader>
			<CardContent className='px-0 pb-0'>
				{loading ? (
					<div className='flex flex-col gap-2 px-6 pb-4'>
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className='h-10 rounded-md bg-muted animate-pulse'
							/>
						))}
					</div>
				) : accounts.length === 0 ? (
					<div className='px-6 pb-4'>
						<StatusPlaceholder
							icon={UserCheck}
							title='No users found'
							description='There are no accounts in the system yet.'
						/>
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Role</TableHead>
								<TableHead>Project access</TableHead>{' '}
								{isCurrentUserAdmin && (
									<TableHead>Change role</TableHead>
								)}{' '}
								<TableHead className='w-24' />
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.length === 0 ? (
								<TableRow>
									<TableCell
										colSpan={isCurrentUserAdmin ? 6 : 5}
										className='text-center text-sm text-muted-foreground py-8'
									>
										No users match your search.
									</TableCell>
								</TableRow>
							) : (
								filtered.map((account) => {
									const isAdmin =
										account.role.toUpperCase() === 'ADMIN';
									const isMember =
										isAdmin ||
										memberAccountIds.has(account.id);
									const isPending = pendingId === account.id;
									return (
										<TableRow key={account.id}>
											<TableCell className='font-medium'>
												{account.name ?? (
													<span className='text-muted-foreground'>
														—
													</span>
												)}
											</TableCell>
											<TableCell className='text-muted-foreground text-sm'>
												{account.email}
											</TableCell>
											<TableCell>
												<Badge
													variant='secondary'
													className='text-xs'
												>
													{formatEnum(account.role)}
												</Badge>
											</TableCell>
											<TableCell>
												{isMember ? (
													<span className='text-xs font-medium text-foreground'>
														Yes
													</span>
												) : (
													<span className='text-xs text-muted-foreground'>
														No
													</span>
												)}
											</TableCell>{' '}
											{isCurrentUserAdmin && (
												<TableCell>
													{account.id !==
													currentUser?.id ? (
														<Select
															value={account.role.toUpperCase()}
															disabled={
																pendingRoleId ===
																account.id
															}
															onValueChange={(
																role,
															) =>
																handleRoleChange(
																	account.id,
																	role,
																)
															}
														>
															<SelectTrigger className='h-7 text-xs w-32'>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																<SelectItem value='ADMIN'>
																	Admin
																</SelectItem>
																<SelectItem value='MODERATOR'>
																	Moderator
																</SelectItem>
																<SelectItem value='VIEWER'>
																	Viewer
																</SelectItem>
															</SelectContent>
														</Select>
													) : (
														<span className='text-xs text-muted-foreground'>
															—
														</span>
													)}
												</TableCell>
											)}{' '}
											{!isAdmin && (
												<TableCell className='text-right'>
													{isMember ? (
														<Button
															variant='ghost'
															size='sm'
															className='h-7 text-xs text-muted-foreground hover:text-destructive'
															disabled={
																isPending ||
																!currentUser ||
																account.id ===
																	currentUser.id
															}
															onClick={() =>
																handleRemove(
																	account.id,
																)
															}
														>
															{isPending
																? 'Removing…'
																: 'Remove'}
														</Button>
													) : (
														<Button
															variant='outline'
															size='sm'
															className='h-7 text-xs'
															disabled={
																isPending ||
																!currentUser
															}
															onClick={() =>
																handleAdd(
																	account.id,
																	account.email,
																)
															}
														>
															{isPending
																? 'Adding…'
																: 'Add'}
														</Button>
													)}
												</TableCell>
											)}
											{isAdmin && <TableCell />}
										</TableRow>
									);
								})
							)}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}
